import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import type { Envelope, Notice, NoticeLevel } from '../../lib/envelope'
import { patchSheets } from '../../lib/sheetCache'
import { comicKeys } from './queries'
import type { ComicSections, ComicSheet } from './types'

type Schema = components['schemas']

export type ComicEnvelope = Envelope<ComicSections>

const NOTICE_LEVELS: NoticeLevel[] = ['info', 'warn', 'error']

function toNotice(raw: Schema['Notice']): Notice {
  const level = raw.level as NoticeLevel | undefined
  return {
    level: level !== undefined && NOTICE_LEVELS.includes(level) ? level : 'info',
    code: raw.code ?? 'unknown',
    message: raw.message ?? '',
  }
}

export function toEnvelope(dto: Schema['ComicEnvelope']): ComicEnvelope {
  return {
    version: dto.version ?? 0,
    changed: (dto.changed ?? {}) as Partial<ComicSections>,
    notices: (dto.notices ?? []).map(toNotice),
  }
}

export interface ComicMutationOptions<TVariables> {
  comicId: number
  request: (variables: TVariables) => Promise<Schema['ComicEnvelope']>
  onEnvelope?: (envelope: ComicEnvelope) => void
  onFailure?: (error: unknown) => void
}

/**
 * A comic has one cache entry — its two sections arrive from the one read — so
 * `patchSheets` is given a single key. It still does the writing rather than a bare
 * `setQueryData`, so the filtering rule is stated once for the whole client.
 *
 * **Nothing here is optimistic, and for a sharper reason than elsewhere.** Every page
 * write moves `core` alongside the scene it changed: `pageCount`, and — when the page is a
 * cover — `hasFrontCover` or `hasBackCover`, which are what the client uses to decide
 * whether the option may be offered again. Guessing those locally and being wrong would
 * offer a second front cover, which is precisely the `409` the flags exist to avoid.
 */
export function useComicMutation<TVariables>(options: ComicMutationOptions<TVariables>) {
  const queryClient = useQueryClient()

  return useMutation<ComicEnvelope, unknown, TVariables>({
    mutationFn: async (variables) => toEnvelope(await options.request(variables)),

    onError: (error) => {
      options.onFailure?.(error)
    },

    onSuccess: (envelope) => {
      patchSheets<ComicSections, ComicSheet>(
        queryClient,
        [comicKeys.sheet(options.comicId)],
        envelope,
      )
      options.onEnvelope?.(envelope)
    },
  })
}

type Envelopes = Schema['ComicEnvelope']

export const comicApi = {
  update: (comicId: number, body: Schema['ComicRequest']) =>
    api.put<Envelopes>(`/comics/${comicId}`, body),

  // ----- scenes -----

  addScene: (comicId: number, body: Schema['ComicSceneRequest']) =>
    api.post<Envelopes>(`/comics/${comicId}/scenes`, body),

  renameScene: (comicId: number, comicSceneId: number, name: string) =>
    api.put<Envelopes>(`/comics/${comicId}/scenes/${comicSceneId}`, { name }),

  removeScene: (comicId: number, comicSceneId: number) =>
    api.delete<Envelopes>(`/comics/${comicId}/scenes/${comicSceneId}`),

  /**
   * Renumbers the comic's scenes. The list must name **every** scene exactly once — the
   * API rejects a partial ordering with a `400` rather than half-applying it, because the
   * unnamed rows would keep stale positions.
   */
  reorderScenes: (comicId: number, ids: number[]) =>
    api.put<Envelopes>(`/comics/${comicId}/scenes/order`, { ids }),

  // ----- pages -----

  addPage: (comicId: number, body: Schema['ComicPageRequest']) =>
    api.post<Envelopes>(`/comics/${comicId}/pages`, body),

  updatePage: (comicId: number, pageId: number, body: Schema['UpdateComicPageRequest']) =>
    api.put<Envelopes>(`/comics/${comicId}/pages/${pageId}`, body),

  removePage: (comicId: number, pageId: number) =>
    api.delete<Envelopes>(`/comics/${comicId}/pages/${pageId}`),

  /**
   * Renumbers **one scene's** pages.
   *
   * The scene rides in the query string rather than the path, and it is **required**: the
   * endpoint looks the scene up before it looks at the ids, so omitting it answers `404`
   * rather than a validation error. The ids must then name every page *in that scene*
   * exactly once — same whole-list rule as the scene ordering above.
   */
  reorderPages: (comicId: number, comicSceneId: number, ids: number[]) =>
    api.put<Envelopes>(`/comics/${comicId}/pages/order`, { ids }, { query: { comicSceneId } }),

  // ----- page images -----

  /**
   * Uploads an image onto a page. JPEG or PNG, 5 MB ceiling, both enforced server-side.
   *
   * Unlike the character and location galleries, this answers a **comic envelope** rather
   * than a gallery — a page image moves the comic's own sections — so it patches the sheet
   * through the same path every other comic write takes.
   *
   * No `Content-Type` is set: a `FormData` body needs the browser to add the multipart
   * boundary itself.
   */
  uploadImage: (comicId: number, pageId: number, file: File, title?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (title !== undefined && title !== '') form.append('title', title)

    return api.post<Envelopes>(`/comics/${comicId}/pages/${pageId}/images`, form)
  },

  /** Deletes a page image and its stored file. Addressed by image id, not by page. */
  removeImage: (comicId: number, imageId: number) =>
    api.delete<Envelopes>(`/comics/${comicId}/pages/images/${imageId}`),
}

/**
 * Starts a page's image generation. **Not a `useComicMutation`** — it answers `202` with a
 * job rather than an envelope, so it belongs to the Stage 8 job machinery and the sheet is
 * refetched once the job settles. See `ComicPageRow`.
 */
export function generatePageImage(
  comicId: number,
  pageId: number,
  body: Schema['GenerateComicImageRequest'],
) {
  return api.post<Schema['GenerationJobDto']>(
    `/comics/${comicId}/pages/${pageId}/images/generate`,
    body,
  )
}
